import { useEffect, useMemo, useState } from "react";
import { Coins, Plus, ArrowDownToLine, ArrowUpFromLine, Sparkles, Bot, Send } from "lucide-react";
import { api, cx, formatErr } from "../lib/api";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";

const PRESETS = [100, 500, 1000, 5000];

/** Provider/User wallet section — balances + recharge stub + recent ledger.
 *  Currently used inside the provider dashboard. */
export default function WalletSection() {
  const { t } = useI18n();
  const { format } = useFx();
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [amount, setAmount] = useState(500);
  const [method, setMethod] = useState("crypto");

  const reload = async () => {
    setError(null);
    try {
      const [w, tx, s] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/transactions?limit=20").catch(() => ({ data: { items: [] } })),
        api.get("/settings/public").catch(() => ({ data: {} })),
      ]);
      setBalances(w.data.balances || []);
      setTransactions(tx.data.items || []);
      setSettings(s.data || {});
    } catch (e) { setError(formatErr(e)); }
  };
  useEffect(() => { reload(); }, []);

  const mintBalance = useMemo(
    () => Number((balances.find((b) => b.currency === "MINT") || {}).balance || 0),
    [balances],
  );

  const requestRecharge = async () => {
    if (!amount || amount <= 0) return;
    setBusy(true); setError(null);
    try {
      // Phase A: persist as a withdrawal-shaped request (status pending);
      // admin marks paid → MINT credited via wallet. We don't have a top-up
      // endpoint yet, so we just stash the intent in localStorage and show
      // a friendly confirmation. Real Stripe flow ships in a later phase.
      const intent = { amount, method, requested_at: new Date().toISOString() };
      const list = JSON.parse(localStorage.getItem("mint_recharge_intents") || "[]");
      list.unshift(intent);
      localStorage.setItem("mint_recharge_intents", JSON.stringify(list.slice(0, 50)));
      setFlash(t("wallet.recharge_pending_body", "Our concierge team will confirm payment and credit your balance shortly."));
      setTimeout(() => setFlash(null), 5000);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="prov-wallet-tab" className="space-y-8">
      <div className="luxe-card p-5 md:p-6">
        <div className="eyebrow">{t("provider_dash.tab_wallet", "Wallet")}</div>
        <h2 className="font-serif-display text-3xl mt-2">
          {t("wallet.section_title", "Wallet")}
        </h2>
        <p className="text-sm text-muted mt-2 max-w-2xl">
          {t("wallet.section_subtitle", "MINT points, crypto and fiat balances all in one place. MINT covers AI concierge usage and team automations.")}
        </p>
        {settings && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="chip text-[11px]" data-testid="wallet-chip-rate">
              {t("wallet.redeem_rate_chip", "1 MINT ≈ €{{rate}}", { rate: Number(settings.mint_eur_rate || 0.01).toFixed(2) })}
            </span>
            <span className="chip text-[11px]" data-testid="wallet-chip-ai">
              <Bot size={11} /> {t("wallet.ai_cost_chip", "AI: {{cost}} MINT / 1k tokens", { cost: settings.mint_per_1k_ai_tokens })}
            </span>
            <span className="chip text-[11px]" data-testid="wallet-chip-automation">
              <Send size={11} /> {t("wallet.automation_cost_chip", "Team msg: {{cost}} MINT each", { cost: settings.team_automation_message_cost_mint })}
            </span>
            {settings.mint_signup_grant > 0 && (
              <span className="chip text-[11px] success" data-testid="wallet-chip-grant">
                <Sparkles size={11} /> {t("wallet.signup_grant_chip", "{{grant}} MINT welcome bonus", { grant: settings.mint_signup_grant })}
              </span>
            )}
          </div>
        )}
      </div>

      {error && <div className="text-rose-300 text-sm" data-testid="wallet-error">{error}</div>}
      {flash && <div className="luxe-card p-3 text-sm text-teal border-teal/40" data-testid="wallet-flash">{flash}</div>}

      {/* Balances */}
      <section className="grid md:grid-cols-[1fr,1fr] gap-6">
        <div>
          <h3 className="font-serif-display text-xl mb-3">{t("wallet.balance_label", "Balance")}</h3>
          {balances.length === 0 ? (
            <div className="luxe-card p-6 text-sm text-muted">{t("wallet.no_balances", "No balances yet — they'll appear here as soon as your first credit lands.")}</div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {balances.map((b) => (
                <div key={b.currency} className="luxe-card p-4" data-testid={`wallet-balance-${b.currency}`}>
                  <div className="label-tiny">{b.currency}</div>
                  <div className="font-serif-display text-2xl mt-1">
                    {b.currency === "MINT" ? `${Number(b.balance || 0).toFixed(0)}` : format(b.balance || 0, b.currency)}
                  </div>
                  {b.currency === "MINT" && settings?.mint_eur_rate ? (
                    <div className="text-[11px] text-muted/70 font-mono mt-0.5">≈ €{(Number(b.balance || 0) * Number(settings.mint_eur_rate)).toFixed(2)}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recharge */}
        <div className="luxe-card p-5" data-testid="wallet-recharge">
          <div className="flex items-center gap-2 mb-1">
            <Coins size={18} className="text-teal" />
            <h3 className="font-serif-display text-xl">{t("wallet.recharge_title", "Recharge MINT")}</h3>
          </div>
          <p className="text-xs text-muted mb-4">
            {t("wallet.recharge_body", "Top up your MINT balance to keep the AI concierge and team-automation reminders running.")}
          </p>
          <label className="block mb-3">
            <span className="label-tiny block mb-1.5">{t("wallet.recharge_amount", "Amount (MINT)")}</span>
            <input
              type="number"
              min="100"
              step="100"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="input-bare"
              data-testid="wallet-amount-input"
            />
          </label>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setAmount(p)}
                className={cx("chip text-[11px] cursor-pointer", amount === p && "border-teal/60 text-teal")}
                data-testid={`wallet-preset-${p}`}
              >
                {p} MINT
                {settings?.mint_eur_rate ? ` · €${(p * Number(settings.mint_eur_rate)).toFixed(2)}` : ""}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              type="button"
              onClick={() => setMethod("crypto")}
              className={cx("tab-pill", method === "crypto" && "active")}
              data-testid="wallet-method-crypto"
            >
              {t("wallet.recharge_crypto", "Crypto (USDT/USDC)")}
            </button>
            <button
              type="button"
              onClick={() => setMethod("card")}
              className={cx("tab-pill", method === "card" && "active")}
              data-testid="wallet-method-card"
            >
              {t("wallet.recharge_card", "Card")}
            </button>
          </div>
          <button
            type="button"
            onClick={requestRecharge}
            disabled={busy || !amount || amount <= 0}
            className={cx("btn btn-primary w-full", busy && "opacity-60")}
            data-testid="btn-wallet-recharge"
          >
            <Plus size={14} /> {t("wallet.recharge_request_btn", "Request recharge")}
          </button>
          <div className="text-[11px] text-muted/70 mt-2 text-center">
            {t("wallet.recharge_pending_title", "Top-up requested.")} {t("wallet.recharge_pending_body", "Our concierge team will confirm payment and credit your balance shortly.")}
          </div>
        </div>
      </section>

      {/* Transactions */}
      <section>
        <h3 className="font-serif-display text-xl mb-3">{t("wallet.tx_recent_title", "Recent transactions")}</h3>
        {transactions.length === 0 ? (
          <div className="luxe-card p-6 text-sm text-muted">{t("wallet.tx_empty", "No transactions yet.")}</div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => (
              <div key={tx.id} className="luxe-card p-3 flex items-center justify-between" data-testid={`wallet-tx-${tx.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cx("h-8 w-8 rounded-full grid place-items-center", tx.amount >= 0 ? "bg-teal/15 text-teal" : "bg-rose-500/15 text-rose-300")}>
                    {tx.amount >= 0 ? <ArrowDownToLine size={14} /> : <ArrowUpFromLine size={14} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{tx.description || tx.reason}</div>
                    <div className="text-[11px] text-muted font-mono">{tx.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                </div>
                <div className={cx("font-mono font-medium", tx.amount >= 0 ? "text-teal" : "text-rose-200")}>
                  {tx.amount >= 0 ? "+" : ""}{Number(tx.amount).toFixed(tx.currency === "MINT" ? 0 : 2)} {tx.currency}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
